import { apiSlice } from '../../app/api/apiSlice'
import { removeEmptyKeys } from 'utilities'

export const studentApiSlice = apiSlice.injectEndpoints({
  tagTypes: ['Students'],
  endpoints: (build) => ({
    getStudents: build.query({
      query: (payload) => {
        const { page = 1, params = {}, limitNumber = 50 } = payload

        const hasParams = Object.entries(params).length !== 0
        return {
          url: `/student/list/${page}/params`,
          params: hasParams
            ? { ...removeEmptyKeys(params), limitNumber }
            : null,
        }
      },
      transformResponse: (response) => {
        return {
          rows: response.result,
          count: response.count,
          pages: response.pages,
        }
      },
      providesTags: ['Students'],
    }),
    getStudent: build.query({
      query: (payload) => {
        const { studentdni, special = false } = payload
        return {
          url: `/student/list/1/params`,
          params: { search: studentdni, special },
        }
      },

      providesTags: ['Students'],
    }),
    getStudentsReadyForApproval: build.query({
      query: (payload) => {
        const { page = 1, params = {}, limitNumber = 50 } = payload

        return {
          url: `/student/aprobal/${page}/params`,
          params: { ...removeEmptyKeys(params), limitNumber },
        }
      },
      transformResponse: (response, meta, arg) => {
        let parsedStudents = response.studentsToAprobal.map((student) => {
          return {
            ...student.student,
            user: student.user,
            documents: student.documents,
          }
        })
        return {
          result: parsedStudents,
          count: response.count,
          page: response.page,
        }
      },

      providesTags: ['Students'],
    }),
    getApprovedStudentDetails: build.query({
      query: (payload) => {
        const { id } = payload

        return {
          url: `/student/aprobal/aproved/${id}`,
        }
      },
      transformResponse: (response, meta, arg) => {
        let dataObject = response.studentsToAprobal[0]
        let studentKeys = Object.keys(dataObject.student)
        let userKeys = Object.keys(dataObject.user)
        let studentFieldKeys = studentKeys.concat(
          userKeys.filter((item) => studentKeys.indexOf(item) < 0)
        )
        let mergedStudentData = studentFieldKeys.reduce((acc, curr) => {
          acc[curr] = dataObject.user[curr] || dataObject.student[curr];
          return acc;
        }, {});
        return {
          ...mergedStudentData,
          documents: response.studentsToAprobal[0].documents,
        }
      },

      providesTags: ['Students'],
    }),
    getRejectReasons: build.query({
      query: () => {
        return {
          url: `/student/aprobal/rejectreasons`,
        }
      },
      providesTags: ['RejectReasons'],
    }),
    getStudentsRejected: build.query({
      query: (payload = {}) => {
        const { page = 1, params = {}, limitNumber = 50 } = payload
        return {
          url: `/student/aprobal/notapproved/${page}/params`,
          params: { ...removeEmptyKeys(params), limitNumber },
        }
      },
      transformResponse: (response, meta, arg) => {
        let parsedStudents = response.studentsNoAproved.map((student) => {
          return {
            ...student.student,
            user: student.user,
            documents: student.documents,
          }
        })
        return {
          result: parsedStudents,
          count: response.count,
          page: response.page,
        }
      },

      providesTags: ['Students'],
    }),
    updateStudent: build.mutation({
      /**
       * API endpoint for updating a single student.
       * @param {Object} payload - 
       *    "studentdni": 31617846,
            "gender": "m",
            "lastname": "G",
            "firstname": "Bastian",
            "birthday": "19-06-1985",
            "email": "noelia.gonzalez@accelone.com",
            "phone": "+5491164652948",
            "schoolcycle": 22,
            "denomination": "",
            "cue": "",
            "cueanexo": "",
            "career": "",
            "grade": "",
            "section": "",
            "shift": "",
            "tutorfirstname": "",
            "tutorlastname": "",
            "tutordnitype": "",
            "tutordni": null,
            "tutorcontact": ""
       * @returns 
       */
      query: (payload) => ({
        url: '/student/list',
        method: 'PUT',
        body: { studentList: [payload] },
      }),
      invalidatesTags: ['Students'],
    }),
    updateStudentAssets: build.mutation({
      query: (payload) => {
        return {
          url: '/student/list/addactive',
          method: 'PUT',
          body: payload,
        }
      },
      invalidatesTags: ['Students'],
    }),
    updateStudentValidationStatus: build.mutation({
      query: (payload) => {
        return {
          url: '/student/aprobal',
          method: 'PUT',
          body: {
            aprobal: payload.approved,
            studentid: payload.studentid,
            ...removeEmptyKeys({
              reason: payload?.reason,
              observation: payload?.observation,
            }),
          },
        }
      },
      invalidatesTags: ['Students'],
    }),
    updateStudentAbleStatus: build.mutation({
      query: (payload) => {
        const { studentdni, able = true } = payload
        return {
          url: '/student/list/able',
          method: 'PUT',
          body: { studentdni, able },
        }
      },
      invalidatesTags: ['Students'],
    }),
    updateStudentSpecialCase: build.mutation({
      query: (payload) => {
        const { studentid, force = false } = payload
        return {
          url: '/student/special/',
          method: 'PUT',
          body: { studentid, force },
        }
      },
      invalidatesTags: ['Students'],
    }),

    getSpecialCases: build.query({
      query: (payload) => {
        const { page = 1, params = {}, limitNumber = 50 } = payload

        const hasParams = Object.entries(params).length !== 0
        return {
          url: `/student/special/${page}/params`,
          params: hasParams
            ? { ...removeEmptyKeys(params), limitNumber }
            : null,
        }
      },
      transformResponse: (response) => {
        let parsedStudents = response.studentsToAprobal.map((student) => {
          return {
            ...student.student,
            user: student.user,
            documents: student.documents,
            bankAccount: student.userbank,
            balance: student.balance,
          }
        })
        return {
          result: parsedStudents,
          count: response.count,
          page: response.page,
        }
      },
      providesTags: ['Students'],
    }),

    getSpecialCase: build.query({
      query: (payload) => {
        const { studentID } = payload

        if (!studentID) return null

        return {
          url: `/student/special/${studentID}`,
        }
      },
      transformResponse: (response, meta, arg) => {
        return {
          ...response?.specialStudent,
          documents: response.documents,
        }
      },
      providesTags: ['Students'],
    }),

    updateSpecialBankAccount: build.mutation({
      query: (payload) => {
        const { studentid, cbu, cuit } = payload
        return {
          url: '/student/special/bankaccount',
          method: 'PUT',
          body: { studentid, cbu, cuit },
        }
      },
      invalidatesTags: ['Students'],
    }),

    uploadSpecialDocument: build.mutation({
      query: (payload) => {
        const { studentid, filename, type, file } = payload
        const formData = new FormData()
        formData.append('studentid', studentid)
        formData.append('filename', filename)
        formData.append('file', file)
        formData.append('type', type)

        return {
          url: '/student/special/specialdocs',
          method: 'POST',
          body: formData,
        }
      },
      invalidatesTags: ['Students'],
    }),

    deleteSpecialDocument: build.mutation({
      query: (payload) => {
        const { studentid, documentid } = payload

        return {
          url: `/student/special/specialdocs/${documentid}`,
          method: 'PUT',
          body: {
            studentid,
          },
        }
      },
      invalidatesTags: ['Students'],
    }),

    sendToBank: build.mutation({
      query: (payload) => {
        const { studentid } = payload

        return {
          url: '/student/special/sendtobank/',
          method: 'POST',
          body: {
            studentid,
          },
        }
      },
      invalidatesTags: ['Students'],
    }),

    getStudentUser: build.query({
      query: (payload) => {
        const { studentdni } = payload

        if (!studentdni) return null

        return {
          url: `/student/user/${studentdni}`,
        }
      },
      transformResponse: (response, meta, arg) => {
        return {
          ...response?.studentInfo,
        }
      },
      providesTags: ['Students'],
    }),

    updateStudentUser: build.mutation({
      query: (payload) => {
        const { studentid, phone, email } = payload
        return {
          url: '/student/user',
          method: 'PATCH',
          body: { studentid, phone, email },
        }
      },
      invalidatesTags: ['Students'],
    }),

    updateStudentUserBankAccount: build.mutation({
      query: (payload) => {
        const { studentid, cbu, cuit } = payload
        return {
          url: '/student/user',
          method: 'PUT',
          body: { studentid, cbu, cuit },
        }
      },
      invalidatesTags: ['Students'],
    }),
  }),

  overrideExisting: true,
})

export const {
  useGetStudentsQuery,
  useLazyGetStudentsQuery,
  useGetStudentQuery,
  useUpdateStudentMutation,
  useUpdateStudentAssetsMutation,
  useGetStudentsReadyForApprovalQuery,
  useGetStudentsRejectedQuery,
  useUpdateStudentValidationStatusMutation,
  useUpdateStudentAbleStatusMutation,
  useGetApprovedStudentDetailsQuery,
  useGetRejectReasonsQuery,
  useUpdateStudentSpecialCaseMutation,
  useGetSpecialCasesQuery,
  useGetSpecialCaseQuery,
  useUpdateSpecialBankAccountMutation,
  useUploadSpecialDocumentMutation,
  useDeleteSpecialDocumentMutation,
  useSendToBankMutation,
  useGetStudentUserQuery,
  useUpdateStudentUserMutation,
  useUpdateStudentUserBankAccountMutation,
} = studentApiSlice
